import {
  Box,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { Link } from "react-router-dom";
import { CompleteStaffCorrectionButton } from "routes/Customer/Waste/Manifests/CompleteStaffCorrectionButton";

import {
  correctedToValue,
  correctionFieldLabel,
  correctionLineLabel,
  correctionPathLabel,
  correctionStateLabel,
  correctionValueLabel,
  isCorrectionClosedUnattributed,
  isCorrectionLanded,
  ManifestCorrectionRow,
} from "./manifestCorrectionDisplay";

type Props = {
  corrections: ManifestCorrectionRow[];
  /** Builds the per-row manifest link-outs. */
  tenantId: string;
  title?: string;
};

/** Correction dates use UTC to match the Manifest Review page. */
export function ManifestCorrectionsTable({
  corrections,
  tenantId,
  title = "Manifest corrections",
}: Props) {
  if (corrections.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" component="h4">
        {title}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Manifest</TableCell>
              <TableCell>Line</TableCell>
              <TableCell>Field</TableCell>
              <TableCell>Corrected to</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Corrected in</TableCell>
              <TableCell>Corrected</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {corrections.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <MuiLink
                    component={Link}
                    to={`/o/${tenantId}/waste/manifests/${c.processedManifestId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {c.manifestTrackingNumber}
                  </MuiLink>
                  {c.shippedDate && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      shipped {getUtcDisplayDate(c.shippedDate, "med")}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{correctionLineLabel(c)}</TableCell>
                <TableCell>{correctionFieldLabel(c.field)}</TableCell>
                <TableCell>{correctionValueLabel(c.field, correctedToValue(c))}</TableCell>
                <TableCell>
                  <StatusChip color={correctionChipColor(c)} label={correctionStateLabel(c)} />
                </TableCell>
                <TableCell>{correctionPathLabel(c.resolutionPath)}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {c.resolvedAt ? getUtcDisplayDate(c.resolvedAt, "med") : "—"}
                  {c.resolvedByName ? ` · ${c.resolvedByName}` : ""}
                </TableCell>
                <TableCell align="right">
                  <CompleteStaffCorrectionButton correction={c} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** Landed reads as done, an unattributed close as a problem, in flight as neutral. */
function correctionChipColor(row: ManifestCorrectionRow): "success" | "warning" | "info" {
  if (isCorrectionLanded(row)) return "success";
  if (isCorrectionClosedUnattributed(row)) return "warning";
  return "info";
}
